import React from 'react';
import { Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import { getMissingRequiredInputHandles } from '../../ProjectPackage/utils/validate';

export default function WorkflowInspector({
  nodes,
  edges,
  selectedId,
  onSelect,
  onClose,
}) {
  const node = nodes.find((n) => n.id === selectedId);
  const realNodes = nodes.filter((n) => !n.data.isTemp);
  const missingFor = (n) =>
    getMissingRequiredInputHandles(
      n.data.inputs,
      edges.filter((e) => e.target === n.id).map((e) => e.targetHandle)
    );
  const missing = realNodes.flatMap((n) =>
    missingFor(n).map((port) => ({ node: n, port }))
  );
  const output = node?.data.output_data;
  const outputPreview =
    output?.thought?.text ||
    output?.handle?.text ||
    output?.result?.preview ||
    output?.combined?.preview ||
    output?.resource?.preview;
  const incoming = node ? edges.filter((e) => e.target === node.id) : [];
  const outgoing = node ? edges.filter((e) => e.source === node.id) : [];
  return (
    <aside className="workflow-inspector" aria-label="Workflow inspector">
      <header className="inspector-heading">
        <span>{node ? 'BLOCK DETAILS' : 'WORKFLOW OUTLINE'}</span>
        <IconButton size="small" onClick={onClose} aria-label="Close inspector">
          <CloseIcon fontSize="small" />
        </IconButton>
      </header>
      <div className="inspector-scroll" key={node?.id || "outline"}>
        {node ? (
          <>
            <div className="inspector-intro">
              <span className="eyebrow">{node.data.type}</span>
              <h2>{node.data.label}</h2>
              <p>
                {String(node.data.deploy_status || 'NOT_DEPLOYED')
                  .toLowerCase()
                  .replaceAll('_', ' ')}
              </p>
              <Button
                startIcon={<TuneIcon />}
                variant="contained"
                fullWidth
                disabled={['DEPLOYING', 'DESTROYING', 'DELETING'].includes(
                  node.data.deploy_status
                )}
                onClick={() => node.data.onOpenModal?.()}
              >
                Configure block
              </Button>
            </div>
            <section>
              <h3>Configuration</h3>
              {Object.entries(node.data.parameter_data || {}).length ? (
                <dl>
                  {Object.entries(node.data.parameter_data || {}).map(
                    ([key, value]) => (
                      <div key={key}>
                        <dt>{key.replaceAll('_', ' ')}</dt>
                        <dd>
                          {/secret|password|token|api.?key/i.test(key)
                            ? '••••••••'
                            : typeof value === 'object'
                              ? JSON.stringify(value)
                              : String(value)}
                        </dd>
                      </div>
                    )
                  )}
                </dl>
              ) : (
                <p>No parameters configured yet.</p>
              )}
            </section>
            <section>
              <h3>Connections</h3>
              <p>
                {incoming.length} incoming · {outgoing.length} outgoing
              </p>
              {[...incoming, ...outgoing].map((e) => {
                const other = nodes.find(
                  (n) => n.id === (e.source === node.id ? e.target : e.source)
                );
                return (
                  <button
                    className="outline-row"
                    key={e.id}
                    onClick={() => onSelect(other?.id)}
                  >
                    <span>{e.target === node.id ? '←' : '→'}</span>
                    <span>
                      {other?.data.label || 'Block'}
                      <small>
                        {e.sourceHandle} → {e.targetHandle}
                      </small>
                    </span>
                  </button>
                );
              })}
            </section>
            {!!missingFor(node).length && (
              <section className="inspector-warning">
                <h3>Inputs to connect</h3>
                {missingFor(node).map((port) => (
                  <p key={port}>{port}</p>
                ))}
              </section>
            )}
            <section>
              <h3>Latest output</h3>
              {node.data.output_data &&
              Object.keys(node.data.output_data).length ? (
                <>
                  {outputPreview && (
                    <p className="output-preview">{outputPreview}</p>
                  )}
                  <details>
                    <summary>Raw output</summary>
                    <pre>{JSON.stringify(node.data.output_data, null, 2)}</pre>
                  </details>
                </>
              ) : (
                <p>Outputs will appear here after a run.</p>
              )}
            </section>
            <button className="text-action" onClick={() => onSelect(null)}>
              ← Back to workflow outline
            </button>
          </>
        ) : (
          <>
            <div className="inspector-intro">
              <span className="eyebrow">IDEA → WORKFLOW</span>
              <h2>
                Give your plan
                <br />a little structure.
              </h2>
              <p>
                Select a block to inspect its configuration, connections, and
                output.
              </p>
            </div>
            <section>
              <h3>
                On this canvas <span>{realNodes.length}</span>
              </h3>
              {realNodes.map((n, i) => (
                <button
                  className="outline-row"
                  key={n.id}
                  onClick={() => onSelect(n.id)}
                >
                  <span className="outline-number">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span>
                    {n.data.label}
                    <small>{n.data.type}</small>
                  </span>
                </button>
              ))}
              {!realNodes.length && (
                <p>Add your first block from the library.</p>
              )}
            </section>
            <section className={missing.length ? 'inspector-warning' : ''}>
              <h3>Connection check</h3>
              <p>
                {missing.length
                  ? `${missing.length} required input${missing.length === 1 ? '' : 's'} to connect.`
                  : 'No missing required inputs.'}
              </p>
              {missing.map(({ node: n, port }) => (
                <button
                  className="outline-row"
                  key={`${n.id}:${port}`}
                  onClick={() => onSelect(n.id)}
                >
                  <span>↳</span>
                  <span>
                    {n.data.label}
                    <small>{port}</small>
                  </span>
                </button>
              ))}
              <p className="inspector-note">
                Checks connections only. Configuration and deployment are
                validated separately.
              </p>
            </section>
          </>
        )}
      </div>
    </aside>
  );
}
